/* Client-side image resize.
 *
 * Garden photos from a modern phone are 3–5 MB and 4000+ px on the long edge.
 * We resize to 1920 px max edge, JPEG quality 0.85, before uploading. This
 * matters because R2's free tier is 5 GB and we project ~1000 photos in v1
 * (see plan/05). 1920 / q=0.85 is comfortably under 600 KB per shot.
 *
 * Why two paths:
 * - The fast path uses `createImageBitmap` with `resizeWidth/resizeHeight`,
 *   which lets the browser scale during decode. iOS Safari otherwise OOMs
 *   the tab when allocating a full 12 MP bitmap before the canvas pass —
 *   the user-visible symptom is "the webpage could not be displayed" after
 *   tapping "Use Photo".
 * - The fallback uses `<img>` + canvas. Slower and more memory-hungry, but
 *   covers older Safari versions that don't expose the resize options.
 */

const MAX_EDGE = 1920;
const JPEG_QUALITY = 0.85;

export interface ResizedImage {
  blob: Blob;
  width: number;
  height: number;
  type: string;
}

export async function resizeForUpload(file: File): Promise<ResizedImage> {
  if (!file.type.startsWith("image/")) {
    throw new Error(`Not an image: ${file.type || "unknown"}`);
  }

  // Read intrinsic dimensions cheaply (no full bitmap allocation).
  const dims = await readImageDimensions(file);
  const longEdge = Math.max(dims.width, dims.height);
  const scale = longEdge > MAX_EDGE ? MAX_EDGE / longEdge : 1;
  const targetWidth = Math.round(dims.width * scale);
  const targetHeight = Math.round(dims.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  let drew = false;
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, {
        resizeWidth: targetWidth,
        resizeHeight: targetHeight,
        resizeQuality: "medium",
        imageOrientation: "from-image",
      });
      try {
        ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
        drew = true;
      } finally {
        bitmap.close();
      }
    } catch {
      // Older Safari rejects unknown options on createImageBitmap;
      // fall through to the <img> path below.
    }
  }

  if (!drew) {
    const url = URL.createObjectURL(file);
    try {
      const img = await loadImage(url);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) throw new Error("Canvas toBlob returned null");

  return { blob, width: targetWidth, height: targetHeight, type: "image/jpeg" };
}

/** Read width/height without decoding the full pixel buffer. */
async function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    return { width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image decode failed"));
    img.src = src;
  });
}
